import * as React from 'react'
import { useEffect, useCallback } from 'react'
import { UserAvatar } from '../Common/UserAvatar'
import { User } from '../../models/user_model'
import { DrawerHeader } from '../DrawerHeader'

export interface UserProfileDrawerUser {
  id: string
  first_name: string
  last_name: string
  email?: string
  color?: string
  avatar?: string | null
  is_men?: boolean
  role?: number
  created_at?: string
}

interface UserProfileDrawerProps {
  isOpen: boolean
  onClose: () => void
  user: UserProfileDrawerUser | null
  /** Optional actions to show at the bottom */
  actions?: React.ReactNode
  /** Whether this is the current logged-in user */
  isCurrentUser?: boolean
  /** Optional callback when profile is clicked */
  onViewProfile?: () => void
}

const getRoleLabel = (role?: number): string => {
  switch (role) {
    case 10: return 'Administrator'
    case 5: return 'Maintainer'
    case 1: return 'Użytkownik'
    case 0: return 'Zablokowany'
    default: return 'Użytkownik'
  }
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('pl-PL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({
  isOpen,
  onClose,
  user,
  actions,
  isCurrentUser = false,
  onViewProfile,
}) => {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  const initials = user
    ? (user.first_name.substring(0, 1) + user.last_name.substring(0, 1)).toUpperCase()
    : ''

  if (!user) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-gray-500/75 transition-opacity z-40 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={`fixed inset-y-0 right-0 z-50 flex transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="relative w-screen sm:max-w-md">
          <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
            <DrawerHeader onClose={onClose} />

            {/* Profile header */}
            <div className="px-6 pt-6 pb-8">
              <div className="flex flex-col items-center text-center">
                {/* Large avatar */}
                <div className="mb-4">
                  <UserAvatar
                    size="2xl"
                    initials={initials}
                    imageUrl={user.avatar}
                    color={user.color}
                    className="shadow-lg"
                  />
                </div>

                {/* Name */}
                <h2 className="text-xl font-semibold text-gray-900">
                  {user.first_name} {user.last_name}
                </h2>

                {/* Role badge */}
                <span className="mt-1 inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                  {getRoleLabel(user.role)}
                </span>
              </div>
            </div>

            {/* Profile details */}
            <div className="flex-1 border-t border-gray-100">
              <dl className="divide-y divide-gray-100">
                {user.email && (
                  <div className="px-6 py-4">
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {user.email}
                    </dd>
                  </div>
                )}

                {user.created_at && (
                  <div className="px-6 py-4">
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {isCurrentUser ? 'Konto utworzone' : 'Dołączył/a'}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(user.created_at)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Actions */}
            {(actions || onViewProfile) && (
              <div className="border-t border-gray-100 px-6 py-4">
                <div className="flex flex-col gap-3">
                  {onViewProfile && (
                    <button
                      type="button"
                      onClick={onViewProfile}
                      className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
                    >
                      {isCurrentUser ? 'Ustawienia profilu' : 'Zobacz profil'}
                    </button>
                  )}
                  {actions}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default UserProfileDrawer
